import type { InstrumentId } from "@trader/shared/MarketData"
import {
  type AmendOrder,
  type Fill,
  formatDecimal,
  MAX_TAKE_PROFITS,
  splitQty,
  type Order,
  type OrderId,
  type Position,
  type Product,
  type SetProtection
} from "@trader/shared/Money"
import { BigDecimal, Option } from "effect"
import { type AccountState, openOrdersOn, openPositionsOn } from "./accountStore.ts"
import { boxLabels } from "../chart/boxLabels.ts"
import { roundToStep } from "./orderTicket.ts"

// Orders and positions on the chart, the way TradingView's trading panel draws them
// (docs/design/0013), without the chart: which lines there are, which can be dragged or
// closed, the profit or loss at a price, and the request a confirmed drag sends.
// TradingLayer draws this and handles the pointer; this module is unit-tested.

type Decimal = BigDecimal.BigDecimal

export type LineKind = "order" | "position" | "stopLoss" | "takeProfit"

export interface ChartLine {
  /** Stable across redraws: the order id, or the position's product. */
  readonly key: string
  readonly kind: LineKind
  readonly price: Decimal
  /** Text on the line's label, e.g. "BUY LMT 10", "LONG 10", "SL". */
  readonly label: string
  /** Buy-ish (blue/green) or sell-ish (red) colouring. */
  readonly tone: "buy" | "sell" | "long" | "short" | "loss" | "profit"
  readonly draggable: boolean
  /** Shows a × (cancel the order, remove the leg, or close the position). */
  readonly closable: boolean
  readonly orderId: OrderId | null
  readonly product: Product
  /** The order's price field a drag moves (limit or stop); null for a position. */
  readonly field: "limitPrice" | "stopPrice" | null
  /**
   * For a position or a working entry: whether it can take a stop-loss / take-profit (shows the SL /
   * TP buttons), and the number the next take-profit gets (a position has up to MAX_TAKE_PROFITS).
   */
  readonly canAdd: { readonly stopLoss: boolean; readonly takeProfit: boolean; readonly takeProfitNumber: number } | null
  /** One of a position's several take-profits (docs/design/0017): the part it closes; null otherwise. */
  readonly qty: Decimal | null
  /** A working entry's own stop-loss / take-profit (not yet on a position): that entry's order id. */
  readonly parentOrderId: OrderId | null
}

const TYPE_LABEL = { Limit: "LMT", Stop: "STP", StopLimit: "STP LMT" } as const

const priceOf = (order: Order): { readonly price: Decimal; readonly field: "limitPrice" | "stopPrice" } | null => {
  switch (order.type._tag) {
    case "Market":
      return null
    case "Limit":
      return { price: order.type.limitPrice, field: "limitPrice" }
    case "Stop":
      return { price: order.type.stopPrice, field: "stopPrice" }
    case "StopLimit":
      // Triggered: only the limit can move; before, the stop is what the user watches.
      return order.status === "Triggered"
        ? { price: order.type.limitPrice, field: "limitPrice" }
        : { price: order.type.stopPrice, field: "stopPrice" }
  }
}

/** Every line the followed account has on this instrument. */
export const chartLines = (state: AccountState | null, instrumentId: InstrumentId | null): ReadonlyArray<ChartLine> => {
  if (state === null || instrumentId === null) return []
  const lines: Array<ChartLine> = []
  const archived = state.summary?.status === "Archived" || state.summary?.endedAt != null
  const positions = openPositionsOn(state, instrumentId)
  for (const order of openOrdersOn(state, instrumentId)) {
    const at = priceOf(order)
    if (at === null) continue
    const pending = order.status === "PendingCancel" || order.status === "PendingSubmit"
    // A strategy's orders are its own; square-offs are the account's.
    const manual = order.runnerId === null && !archived
    const qty = formatDecimal(BigDecimal.subtract(order.qty, order.filledQty))
    if (order.role === "StopLoss" || order.role === "TakeProfit") {
      // Several take-profits are numbered from the entry out (TP1 nearest), with their parts.
      const targets = positions.find((position) => position.product === order.product)?.takeProfits ?? []
      const index = targets.findIndex((target) => target.orderId === order.id)
      const numbered = order.role === "TakeProfit" && targets.length > 1 && index >= 0
      lines.push({
        key: order.id,
        kind: order.role === "StopLoss" ? "stopLoss" : "takeProfit",
        price: at.price,
        label: order.role === "StopLoss" ? "SL" : numbered ? `TP${index + 1}` : "TP",
        qty: numbered ? targets[index]!.qty : null,
        tone: order.role === "StopLoss" ? "loss" : "profit",
        draggable: manual && !pending,
        closable: manual && !pending,
        orderId: order.id,
        product: order.product,
        field: at.field,
        canAdd: null,
        parentOrderId: null
      })
      continue
    }
    const movable = manual && !pending && order.role === "Entry" && !order.clientOrderId.startsWith("close-")
    // The stop-loss and take-profit it will put on the position when it fills (docs/design/0013).
    for (const leg of ["stopLoss", "takeProfit"] as const) {
      const price = order.protection?.[leg]
      if (price === undefined) continue
      lines.push({
        key: `${order.id}:${leg}`,
        kind: leg,
        price,
        label: leg === "stopLoss" ? "SL" : "TP",
        qty: null,
        tone: leg === "stopLoss" ? "loss" : "profit",
        draggable: movable,
        closable: movable,
        orderId: null,
        product: order.product,
        field: null,
        canAdd: null,
        parentOrderId: order.id
      })
    }
    lines.push({
      key: order.id,
      kind: "order",
      price: at.price,
      label: `${order.side === "Buy" ? "BUY" : "SELL"} ${TYPE_LABEL[order.type._tag as keyof typeof TYPE_LABEL]} ${qty}`,
      qty: null,
      tone: order.side === "Buy" ? "buy" : "sell",
      draggable: manual && !pending,
      closable: manual && !pending,
      orderId: order.id,
      product: order.product,
      field: at.field,
      canAdd: movable
        ? { stopLoss: order.protection?.stopLoss === undefined, takeProfit: order.protection?.takeProfit === undefined, takeProfitNumber: 1 }
        : null,
      parentOrderId: null
    })
  }
  for (const position of positions) {
    if (position.avgPrice === null) continue
    const long = BigDecimal.isPositive(position.qty)
    const manual = position.runnerId === null && !archived
    lines.push({
      key: `position:${position.product}`,
      kind: "position",
      price: position.avgPrice,
      label: `${long ? "LONG" : "SHORT"} ${formatDecimal(BigDecimal.abs(position.qty))}`,
      qty: null,
      tone: long ? "long" : "short",
      draggable: false,
      closable: manual,
      orderId: null,
      product: position.product,
      field: null,
      canAdd: manual
        ? {
          stopLoss: position.stopLoss === null,
          takeProfit: position.takeProfits.length < MAX_TAKE_PROFITS,
          takeProfitNumber: position.takeProfits.length + 1
        }
        : null,
      parentOrderId: null
    })
  }
  return lines
}

/** A protected position as TradingView's long / short position tool (docs/design/0016). */
export interface PositionBox {
  readonly product: Product
  readonly long: boolean
  readonly entry: Decimal
  readonly stopLoss: Decimal | null
  /** Nearest the entry first; the green zone reaches the last (docs/design/0017). */
  readonly takeProfits: ReadonlyArray<Decimal>
}

/**
 * A box for each position with a stop-loss or take-profit, from its average price to them. The
 * lines include drafts, so the box follows a stop-loss being dragged or placed before Confirm.
 */
export const positionBoxes = (lines: ReadonlyArray<ChartLine>): Array<PositionBox> => {
  const boxes: Array<PositionBox> = []
  for (const line of lines) {
    if (line.kind !== "position") continue
    // The position's own legs (a working entry's legs have their order as parent).
    const own = (kind: "stopLoss" | "takeProfit") =>
      lines.filter((other) => other.kind === kind && other.parentOrderId === null && other.product === line.product)
    const stopLoss = own("stopLoss")[0]?.price ?? null
    const long = line.tone === "long"
    const takeProfits = own("takeProfit").map((other) => other.price)
      .sort((a, b) => (BigDecimal.equals(a, b) ? 0 : BigDecimal.isLessThan(a, b) === long ? -1 : 1))
    if (stopLoss === null && takeProfits.length === 0) continue
    boxes.push({ product: line.product, long, entry: line.price, stopLoss, takeProfits })
  }
  return boxes
}

/**
 * The position tool's labels for an open position (chart/boxLabels.ts). Several take-profits each
 * show the part they close: the position's split (docs/design/0017), so a target being placed or
 * dragged shows its part before it's confirmed.
 */
export const positionBoxLabels = (position: Position, box: PositionBox, tickSize: Decimal, qtyStep: Decimal, currency: string) => {
  const size = BigDecimal.abs(position.qty)
  const parts = splitQty(size, box.takeProfits.length, qtyStep) ?? box.takeProfits.map(() => size)
  return boxLabels({
    entry: BigDecimal.toNumberUnsafe(box.entry),
    stopLoss: box.stopLoss === null ? null : BigDecimal.toNumberUnsafe(box.stopLoss),
    takeProfits: box.takeProfits.map((price, i) => ({ price: BigDecimal.toNumberUnsafe(price), qty: parts[i]! })),
    qty: position.qty,
    pnl: position.unrealizedPnl,
    pnlName: "Open P&L"
  }, tickSize, currency)
}

/**
 * When the position opened: the fill that took it from flat (or across zero), reading the fills
 * (newest first) back from its quantity; null when the loaded fills don't reach back that far.
 */
export const positionOpenedAt = (fills: ReadonlyArray<Fill>, position: Position): number | null => {
  let rest = position.qty
  for (const fill of fills) {
    if (fill.instrumentId !== position.instrumentId || fill.product !== position.product) continue
    rest = BigDecimal.subtract(rest, fill.side === "Buy" ? fill.qty : BigDecimal.negate(fill.qty))
    if (BigDecimal.isZero(rest) || BigDecimal.isPositive(rest) !== BigDecimal.isPositive(position.qty)) return fill.time
  }
  return null
}

/** A finished paper trade: from the fill that opened the position to the one that closed it. */
export interface ClosedTrade {
  readonly product: Product
  readonly long: boolean
  readonly openedAt: number
  readonly closedAt: number
  /** Average prices of the fills that opened (and added to) it and of those that closed it. */
  readonly entry: number
  readonly exit: number
  /** Its largest size, and the P&L its closing fills realized. */
  readonly qty: Decimal
  readonly pnl: Decimal
  /** The last stop-loss it had (placed while it was open), null for none. */
  readonly stopLoss: number | null
  /**
   * The take-profits it had (placed while it was open and not removed or replaced), nearest the
   * entry first, each with the part it closed or would have (docs/design/0017).
   */
  readonly takeProfits: ReadonlyArray<{ readonly price: number; readonly qty: Decimal }>
}

/**
 * The account's finished trades on an instrument, from its fills (a trade runs from flat to flat;
 * a reversal closes one and opens the next) and the stop-loss / take-profit orders placed while
 * each was open. Only what the loaded fills and order history show.
 */
export const closedTrades = (
  fills: ReadonlyArray<Fill>,
  orders: ReadonlyArray<Order>,
  instrumentId: InstrumentId
): Array<ClosedTrade> => {
  const trades: Array<ClosedTrade> = []
  const n = BigDecimal.toNumberUnsafe
  interface Open {
    readonly long: boolean
    readonly openedAt: number
    qty: number
    entryQty: number
    entryValue: number
    exitQty: number
    exitValue: number
    pnl: Decimal
  }
  const open = new Map<Product, Open>()
  const placedWhileOpen = (product: Product, role: "StopLoss" | "TakeProfit", from: number, to: number) =>
    orders.filter((order) =>
      order.instrumentId === instrumentId && order.product === product && order.role === role &&
      order.createdAt >= from && order.createdAt <= to
    )
  const stopLossOf = (product: Product, from: number, to: number): number | null => {
    let latest: Order | undefined
    for (const order of placedWhileOpen(product, "StopLoss", from, to)) {
      if (latest === undefined || order.createdAt > latest.createdAt || (order.createdAt === latest.createdAt && order.seq > latest.seq)) latest = order
    }
    const at = latest === undefined ? null : priceOf(latest)
    return at === null ? null : n(at.price)
  }
  const takeProfitsOf = (product: Product, long: boolean, from: number, to: number) =>
    placedWhileOpen(product, "TakeProfit", from, to)
      // Ones the user took off or a new set replaced weren't the trade's targets in the end.
      .filter((order) => order.reason !== "Removed" && order.reason !== "Replaced")
      .flatMap((order) => {
        const at = priceOf(order)
        return at === null ? [] : [{ price: n(at.price), qty: order.qty }]
      })
      .sort((a, b) => (a.price - b.price) * (long ? 1 : -1))
  // Oldest first.
  const ordered = fills.filter((fill) => fill.instrumentId === instrumentId).sort((a, b) => a.time - b.time || a.seq - b.seq)
  for (const fill of ordered) {
    const signed = fill.side === "Buy" ? n(fill.qty) : -n(fill.qty)
    const price = n(fill.price)
    let rest = signed
    const current = open.get(fill.product)
    if (current !== undefined) {
      const sign = current.long ? 1 : -1
      if (Math.sign(rest) === sign) {
        current.qty += rest
        current.entryQty += Math.abs(rest)
        current.entryValue += Math.abs(rest) * price
        continue
      }
      const closing = Math.min(Math.abs(rest), Math.abs(current.qty))
      current.exitQty += closing
      current.exitValue += closing * price
      current.pnl = BigDecimal.sum(current.pnl, fill.realizedPnl)
      current.qty += sign * -closing
      rest += sign * closing
      if (Math.abs(current.qty) > 1e-9) continue
      open.delete(fill.product)
      trades.push({
        product: fill.product,
        long: current.long,
        openedAt: current.openedAt,
        closedAt: fill.time,
        entry: current.entryValue / current.entryQty,
        exit: current.exitValue / current.exitQty,
        qty: BigDecimal.fromNumberUnsafe(current.entryQty),
        pnl: current.pnl,
        stopLoss: stopLossOf(fill.product, current.openedAt, fill.time),
        takeProfits: takeProfitsOf(fill.product, current.long, current.openedAt, fill.time)
      })
    }
    // What's left opens a trade (a fresh one, or the other side of a reversal).
    if (Math.abs(rest) > 1e-9) {
      open.set(fill.product, { long: rest > 0, openedAt: fill.time, qty: rest, entryQty: Math.abs(rest), entryValue: Math.abs(rest) * price, exitQty: 0, exitValue: 0, pnl: BigDecimal.fromBigInt(0n) })
    }
  }
  return trades
}

/** The position a line belongs to (its own, or the one an SL / TP protects). */
export const positionFor = (state: AccountState | null, instrumentId: InstrumentId | null, product: Product): Position | undefined =>
  state === null || instrumentId === null ? undefined : openPositionsOn(state, instrumentId).find((position) => position.product === product)

/** The profit (positive) or loss of closing the whole position at `price`, or only `part` of it. */
export const pnlAt = (position: Position, price: Decimal, part?: Decimal): Decimal | null => {
  if (position.avgPrice === null) return null
  const qty = part === undefined ? position.qty : BigDecimal.isNegative(position.qty) ? BigDecimal.negate(part) : part
  return BigDecimal.multiply(BigDecimal.subtract(price, position.avgPrice), qty)
}

/** A chart price (a float from the pointer) on the tick grid, never at or below zero. */
export const snapPrice = (price: number, tickSize: Decimal): Decimal | null => {
  if (!Number.isFinite(price) || price <= 0) return null
  const parsed = BigDecimal.fromNumber(price)
  if (Option.isNone(parsed)) return null
  const snapped = roundToStep(parsed.value, tickSize)
  return BigDecimal.isPositive(snapped) ? snapped : tickSize
}

const ONE_PERCENT = BigDecimal.fromStringUnsafe("0.01")

/**
 * Where a new stop-loss or take-profit starts (before it's dragged), so it starts where the server
 * accepts it:
 * - a take-profit with a stop-loss in place: the next whole R from the average price (TP1 at 1:1,
 *   TP2 at 1:2, …; docs/design/0017), when that is beyond the last price and the other targets;
 * - a further take-profit without one: beyond the farthest by the nearest one's distance;
 * - otherwise 1% beyond the nearer of the average and the last price on its side.
 */
export const startingPrice = (position: Position, leg: "stopLoss" | "takeProfit", tickSize: Decimal): Decimal | null => {
  if (position.avgPrice === null) return null
  const avg = position.avgPrice
  const mark = position.mark ?? avg
  const long = BigDecimal.isPositive(position.qty)
  const below = leg === "stopLoss" ? long : !long
  const beyond = (a: Decimal, b: Decimal) => (below ? BigDecimal.isLessThan(a, b) : BigDecimal.isGreaterThan(a, b))
  const snap = (price: Decimal) => {
    const snapped = roundToStep(price, tickSize)
    return BigDecimal.isPositive(snapped) ? snapped : null
  }
  const shift = (from: Decimal, by: Decimal) => (below ? BigDecimal.subtract(from, by) : BigDecimal.sum(from, by))
  if (leg === "takeProfit") {
    const targets = position.takeProfits.map((target) => target.price)
    const farthest = targets.at(-1)
    const fits = (price: Decimal | null) => price !== null && beyond(price, mark) && (farthest === undefined || beyond(price, farthest))
    if (position.stopLoss !== null) {
      const risk = BigDecimal.abs(BigDecimal.subtract(avg, position.stopLoss.price))
      const atR = BigDecimal.isZero(risk) ? null : snap(shift(avg, BigDecimal.multiply(risk, BigDecimal.fromBigInt(BigInt(targets.length + 1)))))
      if (fits(atR)) return atR
    }
    if (farthest !== undefined) {
      const next = snap(shift(farthest, BigDecimal.abs(BigDecimal.subtract(targets[0]!, avg))))
      if (fits(next)) return next
    }
  }
  const base = below ? BigDecimal.min(avg, mark) : BigDecimal.max(avg, mark)
  const from = leg === "takeProfit" && position.takeProfits.length > 0
    ? (below ? BigDecimal.min(base, position.takeProfits.at(-1)!.price) : BigDecimal.max(base, position.takeProfits.at(-1)!.price))
    : base
  return snap(shift(from, BigDecimal.multiply(base, ONE_PERCENT)))
}

/** What a confirmed drag (or a new SL / TP) sends. */
export type LineRequest =
  | { readonly _tag: "Amend"; readonly orderId: OrderId; readonly prices: AmendOrder }
  | { readonly _tag: "Protect"; readonly instrumentId: InstrumentId; readonly body: SetProtection }

/** A working order or an existing SL / TP moved to `price`: an amend of its order. */
export const moveRequest = (line: ChartLine, price: Decimal): LineRequest | null =>
  line.orderId === null || line.field === null
    ? null
    : { _tag: "Amend", orderId: line.orderId, prices: line.field === "limitPrice" ? { limitPrice: price } : { stopPrice: price } }

/**
 * A position's protection with one leg set (a price) or removed (null), the rest kept as it is.
 * Take-profits (docs/design/0017): `orderId` names the one moved or removed; without it a price adds
 * one (TP2 …) and null removes them all.
 */
export const protectRequest = (
  position: Position,
  leg: "stopLoss" | "takeProfit",
  price: Decimal | null,
  orderId: OrderId | null = null
): LineRequest => {
  const current = position.takeProfits.map((target) => target.price)
  const takeProfits = leg !== "takeProfit"
    ? current
    : orderId !== null
    ? position.takeProfits.flatMap((target) => (target.orderId !== orderId ? [target.price] : price === null ? [] : [price]))
    : price === null
    ? []
    : [...current, price]
  return protectTargetsRequest(position, leg === "stopLoss" ? price : position.stopLoss?.price ?? null, takeProfits)
}

/** A position's stop-loss and its whole list of take-profits (the Positions tab edits the list). */
export const protectTargetsRequest = (position: Position, stopLoss: Decimal | null, takeProfits: ReadonlyArray<Decimal>): LineRequest => ({
  _tag: "Protect",
  instrumentId: position.instrumentId,
  body: { product: position.product, stopLoss, takeProfit: takeProfits[0] ?? null, takeProfits }
})

// --- a working entry's own stop-loss and take-profit (docs/design/0013) ----------------------------

/** The price a working order should fill at (its limit, else its stop); null for a market order. */
export const entryPrice = (order: Order): Decimal | null =>
  order.type._tag === "Limit" || order.type._tag === "StopLimit"
    ? order.type.limitPrice
    : order.type._tag === "Stop"
    ? order.type.stopPrice
    : null

/** The profit (positive) or loss if the order fills at its price and then closes at `price`. */
export const orderPnlAt = (order: Order, price: Decimal): Decimal | null => {
  const entry = entryPrice(order)
  if (entry === null) return null
  const remaining = BigDecimal.subtract(order.qty, order.filledQty)
  const signed = order.side === "Buy" ? remaining : BigDecimal.negate(remaining)
  return BigDecimal.multiply(BigDecimal.subtract(price, entry), signed)
}

/** Where a working order's new stop-loss or take-profit starts: 1% from its price, on its side. */
export const orderStartingPrice = (order: Order, leg: "stopLoss" | "takeProfit", tickSize: Decimal): Decimal | null => {
  const entry = entryPrice(order)
  if (entry === null) return null
  const below = leg === "stopLoss" ? order.side === "Buy" : order.side === "Sell"
  const offset = BigDecimal.multiply(entry, ONE_PERCENT)
  const snapped = roundToStep(below ? BigDecimal.subtract(entry, offset) : BigDecimal.sum(entry, offset), tickSize)
  return BigDecimal.isPositive(snapped) ? snapped : null
}

/** A working order's stop-loss or take-profit set (a price) or removed (null), the other kept. */
export const orderProtectRequest = (order: Order, leg: "stopLoss" | "takeProfit", price: Decimal | null): LineRequest => ({
  _tag: "Amend",
  orderId: order.id,
  prices: {
    protection: {
      stopLoss: leg === "stopLoss" ? price : order.protection?.stopLoss ?? null,
      takeProfit: leg === "takeProfit" ? price : order.protection?.takeProfit ?? null
    }
  }
})

/**
 * A Shift-drag of a working order (TradingView's way): the order to `price`, its stop-loss and
 * take-profit moved by the same amount, in one request.
 */
export const shiftRequest = (order: Order, field: "limitPrice" | "stopPrice", price: Decimal): LineRequest => {
  const entry = entryPrice(order)
  const offset = entry === null ? BigDecimal.fromBigInt(0n) : BigDecimal.subtract(price, entry)
  const moved = (leg: Decimal | undefined) => (leg === undefined ? null : BigDecimal.sum(leg, offset))
  const prices = field === "limitPrice" ? { limitPrice: price } : { stopPrice: price }
  return order.protection === null
    ? { _tag: "Amend", orderId: order.id, prices }
    : {
      _tag: "Amend",
      orderId: order.id,
      prices: { ...prices, protection: { stopLoss: moved(order.protection.stopLoss), takeProfit: moved(order.protection.takeProfit) } }
    }
}

/** The working order with this id on the followed account. */
export const orderById = (state: AccountState | null, orderId: OrderId | null): Order | undefined =>
  state === null || orderId === null ? undefined : state.openOrders.find((order) => order.id === orderId)
